import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { isAxiosError } from 'axios';
import {
  type AuthUser,
  type LoginResult,
  login as apiLogin,
  loginWithTotp as apiLoginWithTotp,
  internalLogin as apiInternalLogin,
  internalLoginWithTotp as apiInternalLoginWithTotp,
  logout as apiLogout,
  fetchCurrentUser,
} from '@/api/auth';
import { LOGOUT_SIGNAL_KEY, setUnauthorizedHandler } from '@/api/client';

export const useAuthStore = defineStore('auth', () => {
  // Session token is now stored in an httpOnly cookie set by the backend.
  // The frontend can't read it; we only track `user` to drive the UI.
  const user = ref<AuthUser | null>(null);
  const initialized = ref(false);
  const sessionUnavailable = ref(false);
  const loggingOut = ref(false);
  const locallyLoggedOut = ref(false);

  const isAuthenticated = computed(() => user.value != null);
  const isInternalUser = computed(() => user.value?.organization_kind === 'internal');
  const featureKeySet = computed(() => new Set(user.value?.feature_keys ?? []));

  // RBAC: workspace owner (parent_user_id null) is always admin. Otherwise
  // depends on the role enum stored on the user row.
  const isAdmin = computed(() => {
    const u = user.value;
    if (!u) return false;
    if (u.parent_user_id == null) return true;
    return u.role === 'admin' || u.role === 'super_admin';
  });
  const canWrite = computed(() => {
    const u = user.value;
    if (!u) return false;
    if (u.parent_user_id == null) return true;
    return u.role === 'admin' || u.role === 'super_admin' || u.role === 'operator';
  });

  function hasFeature(featureKey?: string) {
    if (!featureKey) return true;
    const keys = user.value?.feature_keys;
    if (!keys) return false;
    return featureKeySet.value.has(featureKey);
  }

  async function login(
    identifier: string,
    password: string,
    organizationSlug?: string,
  ): Promise<LoginResult> {
    const res = await apiLogin(identifier, password, organizationSlug);
    if ('totp_required' in res) return res; // caller must follow with completeTotpLogin
    // Backend already set the session cookie; we just mirror the user.
    locallyLoggedOut.value = false;
    user.value = res.user;
    return res;
  }

  async function completeTotpLogin(payload: {
    totp_token: string;
    totp_code?: string;
    recovery_code?: string;
    organization_slug?: string;
  }) {
    const res = await apiLoginWithTotp(payload);
    locallyLoggedOut.value = false;
    user.value = res.user;
    return res;
  }

  async function internalLogin(identifier: string, password: string): Promise<LoginResult> {
    const res = await apiInternalLogin(identifier, password);
    if ('totp_required' in res) return res;
    locallyLoggedOut.value = false;
    user.value = res.user;
    return res;
  }

  async function completeInternalTotpLogin(payload: {
    totp_token: string;
    totp_code?: string;
    recovery_code?: string;
  }) {
    const res = await apiInternalLoginWithTotp(payload);
    locallyLoggedOut.value = false;
    user.value = res.user;
    return res;
  }

  /**
   * Inject a session from an external flow (e.g. accept-invite). The backend
   * has already set the session cookie on its response, so we only need to
   * mirror the user into the store.
   */
  function setSession(_token: string, u: AuthUser) {
    user.value = u;
    initialized.value = true;
  }

  async function logout() {
    // A failed request cannot prove the HttpOnly session was revoked.
    loggingOut.value = true;
    try { await apiLogout(); }
    catch (error) { if (!isAxiosError(error) || error.response?.status !== 401) throw error; }
    finally { loggingOut.value = false; }
    locallyLoggedOut.value = true;
    user.value = null;
    try {
      localStorage.setItem(LOGOUT_SIGNAL_KEY, String(Date.now()));
      localStorage.removeItem(LOGOUT_SIGNAL_KEY);
    } catch { /* Cross-tab notification is best effort. */ }
  }

  function handleUnauthorized() {
    user.value = null;
  }

  async function initializeAuth() {
    if (initialized.value) return;
    sessionUnavailable.value = false;
    try {
      user.value = await fetchCurrentUser();
      locallyLoggedOut.value = false;
      initialized.value = true;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 401) {
        user.value = null; initialized.value = true;
      } else {
        sessionUnavailable.value = true;
      }
    }
  }

  // Wire axios 401 handler (also fires on cross-tab logout signal).
  setUnauthorizedHandler(handleUnauthorized);

  return {
    user,
    sessionUnavailable,
    loggingOut,
    locallyLoggedOut,
    isAuthenticated,
    isInternalUser,
    isAdmin,
    canWrite,
    hasFeature,
    login,
    completeTotpLogin,
    internalLogin,
    completeInternalTotpLogin,
    setSession,
    logout,
    handleUnauthorized,
    initializeAuth,
  };
});
